import * as Sentry from "@sentry/react";

import { Paragraph, StyledAlert } from "App/global-style";
import { Form, FormikProps } from "formik";

import { isMale } from "@storebrand-digital/storebrand-common-validation";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { AlertType } from "Components/Form/Alert";
import { CitizenshipPepFormElements } from "Components/Form/CommonFormElements/CitizenshipPepFormElement";
import { RadioButtonTrueFalse } from "Components/Form/RadioButton";
import { getYesOrNoOptions } from "Components/Form/RadioButton/radioButtonUtils";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { IllustrationName } from "Constants/constants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { nextStep } from "Store/steps/stepsSlice";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { useCustomerNin } from "Store/userIdentification";
import { getError } from "Utils/productUtils";
import React from "react";
import { useDispatch } from "react-redux";
import { PREGNANT_AND_NEWBORN_ABOUT_YOU_STEP } from "..";
import { PregnantAndNewbornIntroFormValues } from "./pregnantAndNewbornProductIntroInterfaces";
import { validationSchema } from "./pregnantAndNewbornProductIntroValidation";

export const PregnantAndNewbornProductIntro = (): React.ReactElement => {
  const isAdvisor = useIsAdvisorSelector();
  const customerNin = useCustomerNin();
  const dispatch = useDispatch();
  const initialFormState = useFormState<PregnantAndNewbornIntroFormValues>(
    PREGNANT_AND_NEWBORN_ABOUT_YOU_STEP.id,
    initialFormValues,
  );
  const [
    {
      formText: { aboutYou },
    },
    lang,
  ] = useProductTexts(
    getFMSProductName(INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN),
  );

  const handleSubmit = (form: PregnantAndNewbornIntroFormValues): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: PREGNANT_AND_NEWBORN_ABOUT_YOU_STEP.id,
        formState: form,
      }),
    );
    dispatch(nextStep());
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN}`,
        );
      }}
    >
      <ProductIntroTile
        illustrationName={IllustrationName.ESTABLISHMENT_FAMILY}
        productBackendName={ProductBackendName.PREGNANT_AND_NEWBORN}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<PregnantAndNewbornIntroFormValues>) => {
          return (
            <Form>
              <Paragraph>{aboutYou.header[lang]}</Paragraph>
              <RadioButtonTrueFalse
                direction="vertical"
                name="parentGuardian"
                id="parentGuardian"
                hint={aboutYou.parentGuardian.hint[lang]}
                label={aboutYou.parentGuardian.labelChild[lang]}
                error={getError("parentGuardian", formProps)}
                options={getYesOrNoOptions(
                  "parentGuardian-yes",
                  "parentGuardian-no",
                )}
              />
              {formProps.values.parentGuardian === BooleanFormType.FALSE && (
                <StyledAlert
                  type="error"
                  alertId="parentGuardian"
                  alertType={AlertType.STOPPER_ALERT}
                  title={aboutYou.parentGuardian.title[lang]}
                  description={aboutYou.parentGuardian.description[lang]}
                  fluid
                />
              )}
              {formProps.values.parentGuardian === BooleanFormType.TRUE &&
                isMale(customerNin) && (
                  <StyledAlert
                    type="warning"
                    alertId="notMother"
                    alertType={AlertType.STOPPER_ALERT}
                    title={aboutYou.notMother.title[lang]}
                    description={aboutYou.notMother.description[lang]}
                    fluid
                  />
                )}
              <CitizenshipPepFormElements formProps={formProps} />
              <PartnerProductIntroForms />
              {!isAdvisor && <MarketingConsent />}
              <ScrollToFirstError />
              <NavigationButtons hidePreviousButton />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN}
                stepId={PREGNANT_AND_NEWBORN_ABOUT_YOU_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues = {
  parentGuardian: undefined,
  isScandinavianCitizen: undefined,
  isStayingInNorwayForMoreThanFiveYears: undefined,
  pep: undefined,
};
